import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { GlossLogomark } from "@/components/GlossLogomark";
import { ArrowRight, ArrowLeft, Phone } from "lucide-react";
import { toast } from "sonner";
import { LOGIN_PAGE } from "@/constants/testIds";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("000000");
  const [busy, setBusy] = useState(false);

  const continueToOtp = () => {
    if (phone.replace(/\D/g, "").length < 10) {
      toast.error("Enter a 10-digit mobile number");
      return;
    }
    setStep("otp");
  };

  const verify = async () => {
    try {
      setBusy(true);
      await login(`+91 ${phone}`, otp);
      toast.success("Welcome to GLOSS");
      navigate("/personas");
    } catch (e) {
      toast.error("Verification failed", { description: "For the demo, use OTP 000000" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2" data-testid={LOGIN_PAGE.root}>
      {/* Left pane — hero */}
      <div className="relative overflow-hidden hidden lg:flex flex-col justify-between p-10"
           style={{
             background:
               "radial-gradient(ellipse 60% 50% at 20% 18%, rgba(212,168,83,0.40) 0%, transparent 60%),"+
               "radial-gradient(ellipse 50% 60% at 80% 80%, rgba(124,92,122,0.45) 0%, transparent 60%),"+
               "linear-gradient(180deg, #2A1E2A 0%, #1A1218 100%)"
           }}>
        <div
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1560066984-138dadb4c035?w=1600&q=80')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            mixBlendMode: "overlay",
          }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(26,18,24,0.35) 0%, rgba(26,18,24,0.75) 100%)" }} />
        <div className="relative flex items-center gap-3">
          <GlossLogomark size={36} animate={false} />
          <span className="font-display font-bold text-white text-[20px] tracking-[-0.02em]">GLOSS</span>
        </div>

        <div className="relative">
          <div className="font-display font-bold text-white" style={{ fontSize: "min(96px, 12vw)", lineHeight: 0.95, letterSpacing: "-0.04em" }}>
            GLOSS
          </div>
          <div className="my-5 w-32 h-px bg-gradient-to-r from-[var(--color-gold-mid)] to-transparent" />
          <div className="font-sora text-white/90 tracking-[0.30em] uppercase text-[13px] font-semibold">
            The operating system for modern salons
          </div>
          <p className="font-inter text-white/70 mt-6 max-w-md text-[15px] leading-relaxed">
            Appointments, stylists, billing, inventory and your waiting display — together in one beautifully calm console.
          </p>
        </div>

        <div className="relative font-inter text-white/55 text-[12px]">Built in India · Made for salons</div>
      </div>

      {/* Right pane */}
      <div className="flex items-center justify-center p-6 lg:p-10" style={{ background: "linear-gradient(180deg,#FDF9F4 0%, #F7F0E8 100%)" }}>
        <div className="w-full max-w-[420px] anim-slide-up-lg">
          <div className="flex flex-col items-center text-center mb-6">
            <GlossLogomark size={64} />
            <h1 className="font-display font-bold text-[28px] text-[var(--color-text-primary)] mt-5">Welcome back</h1>
            <p className="font-inter text-[15px] text-[var(--color-text-muted)] mt-1">Sign in to continue</p>
          </div>

          {step === "phone" && (
            <div className="space-y-4">
              <label className="block">
                <div className="font-inter text-[12.5px] font-medium text-[var(--color-text-secondary)] mb-1.5">Phone number</div>
                <div className="flex items-center h-14 rounded-2xl bg-white border border-[var(--color-text-faint)]/30 focus-within:border-[var(--color-gold-mid)] focus-within:ring-2 focus-within:ring-[var(--color-gold-mid)]/20 transition">
                  <div className="flex items-center gap-2 pl-4 pr-3 border-r border-[var(--color-text-faint)]/30">
                    <Phone size={16} className="text-[var(--color-text-muted)]" />
                    <span className="font-mono text-[15px] text-[var(--color-text-secondary)]">+91</span>
                  </div>
                  <input
                    autoFocus
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    placeholder="98765 43210"
                    className="flex-1 px-4 bg-transparent font-mono text-[16px] outline-none placeholder:text-[var(--color-text-faint)]"
                    data-testid={LOGIN_PAGE.phoneInput}
                  />
                </div>
              </label>
              <button onClick={continueToOtp} className="btn-3d btn-gold-3d btn-lg w-full" data-testid={LOGIN_PAGE.continueBtn}>
                Continue <ArrowRight size={18} />
              </button>
              <p className="font-inter text-[12px] text-center text-[var(--color-text-muted)]">
                Demo: any phone works. The OTP is <span className="font-mono text-[var(--color-mauve-deep)]">000000</span>.
              </p>
            </div>
          )}

          {step === "otp" && (
            <div className="space-y-4">
              <div className="font-inter text-[14px] text-[var(--color-text-secondary)] text-center">
                We sent a code to <span className="font-mono">+91 {phone}</span>
              </div>
              <div className="flex justify-center gap-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <input
                    key={i}
                    value={otp[i] || ""}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, "").slice(0, 1);
                      const next = otp.split("");
                      next[i] = v;
                      setOtp(next.join("").padEnd(6, "").slice(0, 6));
                      if (v && e.target.nextSibling) e.target.nextSibling.focus();
                    }}
                    className="w-12 h-14 rounded-xl border border-[var(--color-text-faint)]/30 bg-white text-center font-mono text-[20px] outline-none focus:border-[var(--color-gold-mid)] focus:ring-2 focus:ring-[var(--color-gold-mid)]/20"
                    data-testid={i === 0 ? LOGIN_PAGE.otpInput : undefined}
                  />
                ))}
              </div>
              <button onClick={verify} disabled={busy} className="btn-3d btn-mauve-3d btn-lg w-full" data-testid={LOGIN_PAGE.verifyBtn}>
                {busy ? "Verifying…" : "Verify"} {!busy && <ArrowRight size={18} />}
              </button>
              <button onClick={() => setStep("phone")} className="w-full font-inter text-[13px] text-[var(--color-text-muted)] flex items-center justify-center gap-1" data-testid={LOGIN_PAGE.backBtn}>
                <ArrowLeft size={14} /> Change number
              </button>
            </div>
          )}

          <div className="mt-10 flex items-center justify-between text-[12px] font-inter text-[var(--color-text-muted)]">
            <a className="hover:text-[var(--color-mauve-deep)] transition" href="#">Need help?</a>
            <a className="hover:text-[var(--color-mauve-deep)] transition flex items-center gap-1" href="#">Book a demo <ArrowRight size={12} /></a>
          </div>
        </div>
      </div>
    </div>
  );
}
